import type {
  EditorialEditorDto,
  DetailSectionDto,
  FinanceEditorDto,
  ServiceEditorDto,
} from "@/lib/schemas/editor";
import type {
  EditorialEditorModel,
  DetailItem,
  EditorMeta,
  EditorOption,
  FinanceEditorModel,
  ServiceEditorModel,
} from "@/lib/domain/editor";
import { toWorkflow } from "./content";
import type { PageSectionEditorModel } from "@/lib/domain/site-content";

export function toDetailSection(section: DetailSectionDto): PageSectionEditorModel {
  return {
    id:section.id,key:section.key,sectionType:section.key,variant:"",imageDimming:70,textAlignment:section.text_alignment,role:section.role,primaryTarget:"",secondaryTarget:"",
    order:section.order,active:section.is_active,eyebrow:section.eyebrow,title:section.title,description:section.description,itemsLabel:"",primaryLabel:"",secondaryLabel:"",items:[],
    video:section.video?{id:section.video.id,mode:section.video.mode,singleSource:section.video.single_source,playlistSource:section.video.playlist_source,playlistCoverSource:section.video.playlist_cover_source,singleCoverAssetId:section.video.single_cover_asset_id,singleCoverUrl:section.video.single_cover_url,singleCoverAlt:section.video.single_cover_alt,playlistCoverAssetId:section.video.playlist_cover_asset_id,playlistCoverUrl:section.video.playlist_cover_url,playlistCoverAlt:section.video.playlist_cover_alt,items:section.video.items.map(item=>({id:item.id,key:item.key,order:item.order,active:item.is_active,source:item.source,title:item.title,description:item.description,coverAssetId:item.cover_asset_id,coverUrl:item.cover_url,coverAlt:item.cover_alt}))}:null,
  };
}

export function detailSectionPayload(section: PageSectionEditorModel) {
  return {id:section.id,key:section.key,order:section.order,is_active:section.active,text_alignment:section.textAlignment,role:section.role,eyebrow:section.eyebrow,title:section.title,description:section.description,video:section.video?{id:section.video.id,mode:section.video.mode,single_source:section.video.singleSource,playlist_source:section.video.playlistSource,playlist_cover_source:section.video.playlistCoverSource,single_cover_asset_id:section.video.singleCoverAssetId,single_cover_alt:section.video.singleCoverAlt,playlist_cover_asset_id:section.video.playlistCoverAssetId,playlist_cover_alt:section.video.playlistCoverAlt,items:section.video.items.map(item=>({id:item.id,key:item.key,order:item.order,is_active:item.active,source:item.source,title:item.title,description:item.description,cover_asset_id:item.coverAssetId,cover_alt:item.coverAlt}))}:null};
}

const detailItems = (
  items: Array<Omit<DetailItem, "clientKey">>,
): DetailItem[] =>
  items.map((item, index) => ({
    ...item,
    clientKey: item.id ? `db-${item.id}` : `loaded-${index}-${item.kind}`,
  }));

const detailItemPayload = (items: DetailItem[]) =>
  items.map(({ clientKey: _clientKey, ...item }) => item);

const option = (value: {
  id: string | number;
  label: string;
  depth?: number;
}): EditorOption => ({
  id: String(value.id),
  label: value.label,
  depth: value.depth,
});

export function editorMeta(
  dto: ServiceEditorDto | FinanceEditorDto | EditorialEditorDto | import("./reference-editor").ReferenceDomainDto | import("./reference-editor").ReferenceCompanyDomainDto,
): EditorMeta {
  return {
    id: dto.id,
    locale: dto.locale,
    image: dto.image,
    version: dto.version,
    workflow: toWorkflow(dto.workflow),
    allowedActions: dto.allowed_actions,
    localeStates: dto.locale_states.map((row) => ({
      code: row.code,
      label: row.label,
      hasTranslation: row.has_translation,
      published: row.published,
      workflow: toWorkflow(row.workflow),
    })),
    revisions: dto.revisions.map((row) => ({
      version: row.version,
      schemaVersion: row.schema_version,
      createdAt: new Date(row.created_at),
      createdBy: row.created_by,
      changedFields: row.changed_fields,
      draft: row.is_draft,
      live: row.is_live,
    })),
    options: {
      services: dto.options.services.map(option),
      financeSolutions: dto.options.finance_solutions.map(option),
      sectors: dto.options.sectors.map(option),
      classifications: dto.options.classifications.map(option),
      references: dto.options.references.map(option),
      authors: dto.options.authors.map(option),
      editorialCollections: dto.options.editorial_collections.map(option),
      editorialCategories: dto.options.editorial_categories.map(option),
      editorialTags: dto.options.editorial_tags.map(option),
      financeCategories: dto.options.finance_categories.map(option),
      serviceCategories: dto.options.service_categories.map(option),
      companies: dto.options.companies.map(option),
    },
  };
}

const translation = (value: {
  title: string;
  slug: string;
  summary: string;
  image_alt: string;
  seo_title: string;
  seo_description: string;
}) => ({
  title: value.title,
  slug: value.slug,
  summary: value.summary,
  imageAlt: value.image_alt,
  seoTitle: value.seo_title,
  seoDescription: value.seo_description,
});

export function toServiceEditor(dto: ServiceEditorDto): ServiceEditorModel {
  return {
    ...editorMeta(dto),
    kind: "service",
    record: {
      code: dto.record.code,
      categoryId: dto.record.category_id,
      parentId: dto.record.parent_id,
      depth: dto.record.depth,
      icon: dto.record.icon,
      order: dto.record.order,
      archived: dto.record.is_archived,
      mediaAssetId: dto.record.media_asset_id,
    },
    translation: translation(dto.translation),
    content: {
      body: dto.content.body,
      items: detailItems(dto.content.items),
      sections: dto.content.sections.map((section) => ({
        id: section.id, key: section.key, sectionType: section.key === "video" ? "video" : section.key,
        variant: "", imageDimming: 70, textAlignment: section.text_alignment, role: section.role, primaryTarget: "", secondaryTarget: "",
        order: section.order, active: section.is_active, eyebrow: section.eyebrow, title: section.title,
        description: section.description, itemsLabel: "", primaryLabel: "", secondaryLabel: "", items: [],
        video: section.video ? {
          id: section.video.id, mode: section.video.mode, singleSource: section.video.single_source,
          playlistSource: section.video.playlist_source, playlistCoverSource: section.video.playlist_cover_source,
          singleCoverAssetId: section.video.single_cover_asset_id, singleCoverUrl: section.video.single_cover_url,
          singleCoverAlt: section.video.single_cover_alt, playlistCoverAssetId: section.video.playlist_cover_asset_id,
          playlistCoverUrl: section.video.playlist_cover_url, playlistCoverAlt: section.video.playlist_cover_alt,
          items: section.video.items.map((item) => ({ id:item.id,key:item.key,order:item.order,active:item.is_active,source:item.source,title:item.title,description:item.description,coverAssetId:item.cover_asset_id,coverUrl:item.cover_url,coverAlt:item.cover_alt })),
        } : null,
      })),
    },
    relations: {
      relatedServiceIds: dto.relations.related_service_ids,
      financeSolutionIds: dto.relations.finance_solution_ids,
    },
  };
}

export function toFinanceEditor(dto: FinanceEditorDto): FinanceEditorModel {
  return {
    ...editorMeta(dto),
    kind: "finance",
    record: {
      code: dto.record.code,
      categoryId: dto.record.category_id,
      order: dto.record.order,
      archived: dto.record.is_archived,
      mediaAssetId: dto.record.media_asset_id,
    },
    translation: {
      ...translation(dto.translation),
      body: dto.translation.body,
    },
    content: {
      items: detailItems(dto.content.items),
      sections: dto.content.sections.map((section) => ({
        id: section.id, key: section.key, sectionType: section.key, variant: "", imageDimming: 70, textAlignment: section.text_alignment, role: section.role, primaryTarget: "", secondaryTarget: "", order: section.order, active: section.is_active,
        eyebrow: section.eyebrow, title: section.title, description: section.description, itemsLabel: "", primaryLabel: "", secondaryLabel: "", items: [],
        video: section.video ? { id:section.video.id, mode:section.video.mode, singleSource:section.video.single_source, playlistSource:section.video.playlist_source, playlistCoverSource:section.video.playlist_cover_source, singleCoverAssetId:section.video.single_cover_asset_id, singleCoverUrl:section.video.single_cover_url, singleCoverAlt:section.video.single_cover_alt, playlistCoverAssetId:section.video.playlist_cover_asset_id, playlistCoverUrl:section.video.playlist_cover_url, playlistCoverAlt:section.video.playlist_cover_alt, items:section.video.items.map((item)=>({id:item.id,key:item.key,order:item.order,active:item.is_active,source:item.source,title:item.title,description:item.description,coverAssetId:item.cover_asset_id,coverUrl:item.cover_url,coverAlt:item.cover_alt})) } : null,
      })),
    },
    relations: {},
    comparison: {
      categoryCode: dto.comparison.category_code,
      profileEnabled: dto.comparison.profile_enabled,
      published: dto.comparison.published,
      publishedAsOf: dto.comparison.published_as_of,
      versions: dto.comparison.versions.map((version) => ({
        editionId: version.edition_id,
        code: version.code,
        status: version.status,
        statusLabel: version.status_label,
        asOfDate: version.as_of_date,
        isCurrent: version.is_current,
        rows: version.rows.map((row) => ({
          criterionKey: row.criterion_key,
          group: row.group,
          label: row.label,
          value: row.value,
          note: row.note,
          missing: row.missing,
        })),
      })),
    },
  };
}

export function toEditorialEditor(
  dto: EditorialEditorDto,
): EditorialEditorModel {
  return {
    ...editorMeta(dto),
    kind: "editorial",
    record: {
      format: dto.record.format,
      authorId: dto.record.author_id,
      categoryId: dto.record.category_id,
      collectionIds: dto.record.collection_ids,
      tagIds: dto.record.tag_ids,
      sectorIds: dto.record.sector_ids,
      order: dto.record.order,
      featured: dto.record.is_featured,
      archived: dto.record.is_archived,
      mediaAssetId: dto.record.media_asset_id,
    },
    translation: translation(dto.translation),
    content: {
      body: dto.content.body,
      transcript: dto.content.transcript,
      outcomes: dto.content.outcomes,
      videoUrl: dto.content.video_url,
      durationSeconds: dto.content.duration_seconds,
      resourceUrl: dto.content.resource_url,
      resourceVersion: dto.content.resource_version,
      eventDate: dto.content.event_date,
      durationMinutes: dto.content.duration_minutes,
      sourceUrl: dto.content.source_url,
      publicationName: dto.content.publication_name,
      resourceMediaAssetId: dto.content.resource_media_asset_id,
      sections: dto.content.sections.map(toDetailSection),
    },
  };
}

export function serviceEditorPayload(model: ServiceEditorModel) {
  return {
    locale: model.locale,
    version: model.version,
    record: {
      code: model.record.code,
      category_id: model.record.categoryId,
      parent_id: model.record.parentId,
      icon: model.record.icon,
      order: model.record.order,
      media_asset_id: model.record.mediaAssetId,
    },
    translation: {
      title: model.translation.title,
      slug: model.translation.slug,
      summary: model.translation.summary,
      image_alt: model.translation.imageAlt,
      seo_title: model.translation.seoTitle,
      seo_description: model.translation.seoDescription,
    },
    content: {
      body: model.content.body,
      items: detailItemPayload(model.content.items),
      sections: model.content.sections.map((section) => ({
        id: section.id, key: section.key, order: section.order, is_active: section.active, text_alignment: section.textAlignment, role: section.role,
        eyebrow: section.eyebrow, title: section.title, description: section.description,
        video: section.video ? {
          id:section.video.id, mode:section.video.mode, single_source:section.video.singleSource,
          playlist_source:section.video.playlistSource, playlist_cover_source:section.video.playlistCoverSource,
          single_cover_asset_id:section.video.singleCoverAssetId, single_cover_alt:section.video.singleCoverAlt,
          playlist_cover_asset_id:section.video.playlistCoverAssetId, playlist_cover_alt:section.video.playlistCoverAlt,
          items:section.video.items.map((item)=>({id:item.id,key:item.key,order:item.order,is_active:item.active,source:item.source,title:item.title,description:item.description,cover_asset_id:item.coverAssetId,cover_alt:item.coverAlt})),
        } : null,
      })),
    },
    relations: {
      related_service_ids: model.relations.relatedServiceIds,
      finance_solution_ids: model.relations.financeSolutionIds,
    },
  };
}
export function financeEditorPayload(model: FinanceEditorModel) {
  return {
    locale: model.locale,
    version: model.version,
    record: {
      code: model.record.code,
      category_id: model.record.categoryId,
      order: model.record.order,
      media_asset_id: model.record.mediaAssetId,
    },
    translation: {
      title: model.translation.title,
      slug: model.translation.slug,
      summary: model.translation.summary,
      body: model.translation.body,
      image_alt: model.translation.imageAlt,
      seo_title: model.translation.seoTitle,
      seo_description: model.translation.seoDescription,
    },
    content: {
      items: detailItemPayload(model.content.items),
      sections: model.content.sections.map((section) => ({
        id: section.id, key: section.key, order: section.order, is_active: section.active, text_alignment: section.textAlignment, role: section.role, eyebrow: section.eyebrow, title: section.title, description: section.description,
        video: section.video ? { id:section.video.id, mode:section.video.mode, single_source:section.video.singleSource, playlist_source:section.video.playlistSource, playlist_cover_source:section.video.playlistCoverSource, single_cover_asset_id:section.video.singleCoverAssetId, single_cover_alt:section.video.singleCoverAlt, playlist_cover_asset_id:section.video.playlistCoverAssetId, playlist_cover_alt:section.video.playlistCoverAlt, items:section.video.items.map((item)=>({id:item.id,key:item.key,order:item.order,is_active:item.active,source:item.source,title:item.title,description:item.description,cover_asset_id:item.coverAssetId,cover_alt:item.coverAlt})) } : null,
      })),
    },
    relations: {},
  };
}
export function editorialEditorPayload(model: EditorialEditorModel) {
  return {
    locale: model.locale,
    version: model.version,
    record: {
      format: model.record.format,
      author_id: model.record.authorId,
      category_id: model.record.categoryId,
      collection_ids: model.record.collectionIds,
      tag_ids: model.record.tagIds,
      sector_ids: model.record.sectorIds,
      order: model.record.order,
      is_featured: model.record.featured,
      media_asset_id: model.record.mediaAssetId,
    },
    translation: {
      title: model.translation.title,
      slug: model.translation.slug,
      summary: model.translation.summary,
      image_alt: model.translation.imageAlt,
      seo_title: model.translation.seoTitle,
      seo_description: model.translation.seoDescription,
    },
    content: {
      body: model.content.body,
      transcript: model.content.transcript,
      outcomes: model.content.outcomes,
      video_url: model.content.videoUrl,
      duration_seconds: model.content.durationSeconds,
      resource_media_asset_id: model.content.resourceMediaAssetId,
      resource_version: model.content.resourceVersion,
      event_date: model.content.eventDate,
      duration_minutes: model.content.durationMinutes,
      source_url: model.content.sourceUrl,
      publication_name: model.content.publicationName,
      sections: model.content.sections.map(detailSectionPayload),
    },
  };
}
